const NATIONS = ['england', 'scotland', 'wales'];
const DEFAULT_NATION = 'england';

const NATION_LABELS = {
  england: 'England',
  scotland: 'Scotland',
  wales: 'Wales'
};

const pad = (value) => String(value).padStart(2, '0');

const toKey = (date) => (
  `${date.getUTCFullYear()}-${pad(date.getUTCMonth() + 1)}-${pad(date.getUTCDate())}`
);

const parseKey = (key) => {
  const [year, month, day] = String(key || '').split('-').map(Number);
  return new Date(Date.UTC(year, month - 1, day));
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

export const parseNation = (value) => {
  const nation = String(value || '').trim().toLowerCase();
  return NATIONS.includes(nation) ? nation : '';
};

const easterSunday = (year) => {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(Date.UTC(year, month - 1, day));
};

const nthMonday = (year, month, n) => {
  const first = new Date(Date.UTC(year, month - 1, 1));
  const dow = first.getUTCDay();
  const offset = dow === 0 ? 1 : (dow === 1 ? 0 : 8 - dow);
  first.setUTCDate(1 + offset + ((n - 1) * 7));
  return first;
};

const lastMonday = (year, month) => {
  const last = new Date(Date.UTC(year, month, 0));
  const dow = last.getUTCDay();
  const back = dow === 0 ? 6 : dow - 1;
  last.setUTCDate(last.getUTCDate() - back);
  return last;
};

const observeWeekdays = (year, month, days) => {
  const taken = new Set();
  const observed = [];
  days.forEach((day) => {
    let date = new Date(Date.UTC(year, month - 1, day));
    while (date.getUTCDay() === 0 || date.getUTCDay() === 6 || taken.has(toKey(date))) {
      date = addDays(date, 1);
    }
    taken.add(toKey(date));
    observed.push(date);
  });
  return observed;
};

const bankHolidaySet = (year, nation) => {
  const resolved = parseNation(nation) || DEFAULT_NATION;
  const easter = easterSunday(year);
  const keys = new Set();
  const add = (date) => keys.add(toKey(date));

  observeWeekdays(year, 1, resolved === 'scotland' ? [1, 2] : [1]).forEach(add);
  add(addDays(easter, -2));
  if (resolved !== 'scotland') add(addDays(easter, 1));
  add(nthMonday(year, 5, 1));
  add(lastMonday(year, 5));
  if (resolved === 'scotland') {
    add(nthMonday(year, 8, 1));
    observeWeekdays(year, 11, [30]).forEach(add);
  } else {
    add(lastMonday(year, 8));
  }
  observeWeekdays(year, 12, [25, 26]).forEach(add);
  return keys;
};

const holidayCache = new Map();

const holidaysForYears = (startYear, endYear, nation) => {
  const resolved = parseNation(nation) || DEFAULT_NATION;
  const keys = new Set();
  for (let year = startYear; year <= endYear; year += 1) {
    const cacheKey = `${resolved}-${year}`;
    let yearKeys = holidayCache.get(cacheKey);
    if (!yearKeys) {
      yearKeys = bankHolidaySet(year, resolved);
      holidayCache.set(cacheKey, yearKeys);
    }
    yearKeys.forEach((key) => keys.add(key));
  }
  return keys;
};

export const isWeekend = (key) => {
  if (!key) return false;
  const day = parseKey(key).getUTCDay();
  return day === 0 || day === 6;
};

export const isBankHoliday = (key, nation) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(key || '')) return false;
  const year = Number(key.slice(0, 4));
  return holidaysForYears(year, year, nation).has(key);
};

export const isWorkingDay = (key, nation) => !isWeekend(key) && !isBankHoliday(key, nation);

export const workingDayCount = (startKey, endKey, nation) => {
  if (!startKey || !endKey || startKey > endKey) return 0;
  const holidays = holidaysForYears(Number(startKey.slice(0, 4)), Number(endKey.slice(0, 4)), nation);
  const cursor = parseKey(startKey);
  const end = parseKey(endKey);
  let count = 0;
  while (cursor <= end) {
    const key = toKey(cursor);
    const day = cursor.getUTCDay();
    if (day !== 0 && day !== 6 && !holidays.has(key)) count += 1;
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }
  return count;
};

export { NATIONS, DEFAULT_NATION, NATION_LABELS };
